import type BpmnModeler from 'bpmn-js/lib/Modeler'
import type NavigatedViewer from 'bpmn-js/lib/NavigatedViewer'
import { useEffect, useRef } from 'react'
import type { IssueType, SummaryItem } from '@/components/analysis/summary/types/triage'
import {
	chooseImprovementBadge,
	type ImprovementBadgeChoice,
} from '@/components/bpmn/improvements/chooseImprovementBadge'
import {
	ADDRESSED_CHECK_SVG,
	ADDRESSED_MARKER_CLASS,
	IMPROVEMENT_MARKER_CLASSES,
	styleForType,
} from '@/components/bpmn/improvements/improvementTypeStyles'
import type { ImprovementStatusGetter } from '@/components/bpmn/improvements/mapImprovementsToElements'
import { useTranslation } from '@/hooks/useTranslation'

const OVERLAY_TYPE = 'improvement-badge'

type BpmnViewer = BpmnModeler | InstanceType<typeof NavigatedViewer>

interface OverlaysService {
	add: (
		elementId: string,
		type: string,
		config: {
			position: { top?: number; right?: number; bottom?: number; left?: number }
			html: HTMLElement
		}
	) => string
	remove: (filter: { type: string }) => void
}

interface CanvasService {
	addMarker: (elementId: string, marker: string) => void
	removeMarker: (elementId: string, marker: string) => void
}

interface ElementLike {
	id: string
	type: string
	width?: number
}

interface ElementRegistryService {
	filter: (fn: (el: ElementLike) => boolean) => ElementLike[]
	get: (id: string) => unknown
}

interface EventBusService {
	on: (event: string, callback: () => void) => void
	off: (event: string, callback: () => void) => void
}

export interface ImprovementOverlayActivate {
	bpmnId: string
	button: HTMLButtonElement
	items: SummaryItem[]
	badge: NonNullable<ImprovementBadgeChoice>
}

function markerForType(type: IssueType): string {
	return styleForType(type).markerClass
}

function clearMarkers(canvas: CanvasService, ids: Iterable<string>) {
	for (const id of ids) {
		for (const marker of IMPROVEMENT_MARKER_CLASSES) {
			try {
				canvas.removeMarker(id, marker)
			} catch {
				// Element may have been deleted.
			}
		}
	}
}

export function useImprovementOverlays(
	modeler: BpmnViewer | null,
	xmlLoaded: boolean,
	grouped: Map<string, SummaryItem[]>,
	getStatus: ImprovementStatusGetter,
	visible: boolean,
	orderedItems: SummaryItem[],
	onActivate: (payload: ImprovementOverlayActivate) => void,
	badgeElementsRef: { current: Map<string, HTMLButtonElement> }
): void {
	const { t } = useTranslation()
	const onActivateRef = useRef(onActivate)
	onActivateRef.current = onActivate
	const getStatusRef = useRef(getStatus)
	getStatusRef.current = getStatus
	const orderedItemsRef = useRef(orderedItems)
	orderedItemsRef.current = orderedItems
	const markedIdsRef = useRef<Set<string>>(new Set())

	useEffect(() => {
		if (!modeler || !xmlLoaded) return

		const overlays = modeler.get('overlays') as OverlaysService
		const canvas = modeler.get('canvas') as CanvasService
		const elementRegistry = modeler.get('elementRegistry') as ElementRegistryService
		const eventBus = modeler.get('eventBus') as EventBusService

		function renderOverlays() {
			overlays.remove({ type: OVERLAY_TYPE })
			clearMarkers(canvas, markedIdsRef.current)
			markedIdsRef.current = new Set()
			badgeElementsRef.current.clear()

			if (!visible) return

			const getStatusNow = getStatusRef.current

			for (const [bpmnId, items] of grouped) {
				if (!elementRegistry.get(bpmnId)) continue
				const badge = chooseImprovementBadge(items, getStatusNow)
				if (!badge) continue

				try {
					canvas.addMarker(
						bpmnId,
						badge.kind === 'open' ? markerForType(badge.type) : ADDRESSED_MARKER_CLASS
					)
					markedIdsRef.current.add(bpmnId)
				} catch {
					continue
				}

				const style = badge.kind === 'open' ? styleForType(badge.type) : null
				const button = document.createElement('button')
				button.type = 'button'
				button.className =
					badge.kind === 'addressed'
						? 'bpmn-improvement-badge bpmn-improvement-badge--addressed'
						: `bpmn-improvement-badge ${style?.badgeClass ?? ''}`

				const typeLabel =
					badge.kind === 'open'
						? t(styleForType(badge.type).labelKey)
						: t('pages.analysisDetail.structuredSummaryEditor.triage.addressed')
				const globalIndex = orderedItemsRef.current.findIndex((item) => item.id === badge.primary.id)
				const n = globalIndex >= 0 ? globalIndex + 1 : 1
				const stateLabel =
					badge.kind === 'open'
						? t('components.bpmnEditor.improvements.badgeStateOpen')
						: t('components.bpmnEditor.improvements.badgeStateAddressed')
				button.setAttribute(
					'aria-label',
					t('components.bpmnEditor.improvements.badgeAria', {
						type: typeLabel,
						n,
						state: stateLabel,
					})
				)

				const icon = document.createElement('span')
				icon.className = 'bpmn-improvement-badge__icon'
				icon.setAttribute('aria-hidden', 'true')
				icon.innerHTML = badge.kind === 'open' ? styleForType(badge.type).iconSvg : ADDRESSED_CHECK_SVG
				button.appendChild(icon)

				if (badge.kind === 'open' && badge.extraCount > 0) {
					const extra = document.createElement('span')
					extra.className = 'bpmn-improvement-badge__extra'
					extra.textContent = `+${badge.extraCount}`
					button.appendChild(extra)
				}

				const activate = (event: Event) => {
					event.preventDefault()
					event.stopPropagation()
					onActivateRef.current({
						bpmnId,
						button,
						items,
						badge,
					})
				}
				button.addEventListener('click', activate)
				button.addEventListener('keydown', (event: KeyboardEvent) => {
					if (event.key === 'Enter' || event.key === ' ') {
						activate(event)
					}
				})

				badgeElementsRef.current.set(bpmnId, button)

				overlays.add(bpmnId, OVERLAY_TYPE, {
					position: { top: -6, right: 10 },
					html: button,
				})
			}
		}

		renderOverlays()
		eventBus.on('commandStack.changed', renderOverlays)

		return () => {
			eventBus.off('commandStack.changed', renderOverlays)
			overlays.remove({ type: OVERLAY_TYPE })
			clearMarkers(canvas, markedIdsRef.current)
			markedIdsRef.current = new Set()
			badgeElementsRef.current.clear()
		}
	}, [modeler, xmlLoaded, visible, grouped, t, badgeElementsRef])
}
